import { toast } from '../../common_components/Toast/ToastUtils';
import XLSX from 'xlsx-js-style';
const titleStyle = {
    font: { bold: true, sz: 20, color: { rgb: '181D27' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const columnHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: '374151' } },
    fill: { fgColor: { rgb: 'E5E7EB' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const groupHeaderStyle = {
    font: { bold: true, sz: 12, color: { rgb: '1F2937' } },
    fill: { fgColor: { rgb: 'D1D5DB' } },
    alignment: { horizontal: 'center', vertical: 'center' },
};
const summaryRowStyle = {
    font: { bold: true, sz: 11, color: { rgb: '181D27' } },
    fill: { fgColor: { rgb: 'F3F4F6' } },
    alignment: { horizontal: 'right', vertical: 'center' },
};
const positiveVarianceStyle = {
    font: { color: { rgb: '027A48' } },
    alignment: { horizontal: 'center' },
};
const negativeVarianceStyle = {
    font: { color: { rgb: 'B42318' } },
    alignment: { horizontal: 'center' },
};
export function exportAffiliateProjection(rows, totalSummary, month, clientType) {
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
        toast.info('No data available to export');
        return;
    }
    try {
        const formatMonth = (m) => {
            if (!m)
                return '';
            const [year, mon] = m.split('-');
            const date = new Date(parseInt(year), parseInt(mon) - 1);
            return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        };
        const formatVariance = (value) => {
            const num = Number(value) || 0;
            return `${Math.abs(num).toFixed(2)}%`;
        };
        const formatCurrency = (value, withSymbol = true) => {
            const num = Number(value) || 0;
            const formatted = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return withSymbol ? `$${formatted}` : formatted;
        };
        const isOrders = (accountType) => (accountType || '').toLowerCase() === 'orders';
        // Summary aggregates order counts (not revenue) only when the "Orders" type is selected.
        const isOrdersView = isOrders(clientType);
        // Group headers row
        const groupHeaders = ['', '', 'Platform', '', '', '', 'Affiliate', '', '', '', 'Commission'];
        // Column headers
        const headers = [
            'Client', 'AM',
            'MC', 'Goal', 'MTD', 'Variance',
            'MC', 'Goal', 'MTD', 'Variance',
            'Baseline',
        ];
        // Data rows
        const data = rows.map((row) => [
            row.name || '',
            row.am || 'NA',
            row.platform?.mc || '',
            formatCurrency(row.platform?.goal),
            formatCurrency(row.platform?.mtd),
            formatVariance(row.platform?.variance_percent),
            Array.isArray(row.affiliate?.mc) ? row.affiliate.mc.join(', ') : (row.affiliate?.mc || ''),
            formatCurrency(row.affiliate?.goal, !isOrders(row.account_type)),
            formatCurrency(row.affiliate?.mtd, !isOrders(row.account_type)),
            formatVariance(row.affiliate?.variance_percent),
            formatCurrency(row.commission?.baseline),
        ]);
        // Summary row
        const summaryRow = [
            'Total Summary', '',
            '',
            formatCurrency(totalSummary?.platform?.goal),
            formatCurrency(totalSummary?.platform?.mtd),
            formatVariance(totalSummary?.platform?.variance_percent),
            '',
            formatCurrency(totalSummary?.affiliate?.goal, !isOrdersView),
            formatCurrency(totalSummary?.affiliate?.mtd, !isOrdersView),
            formatVariance(totalSummary?.affiliate?.variance_percent),
            formatCurrency(totalSummary?.commission?.baseline),
        ];
        const numCols = headers.length;
        const monthDisplay = formatMonth(month);
        const titleText = `Affiliate Account Performance${monthDisplay ? ` - ${monthDisplay}` : ''}`;
        const titleRow = [titleText, ...Array(numCols - 1).fill('')];
        const spacerRow = Array(numCols).fill('');
        const sheetData = [titleRow, spacerRow, groupHeaders, headers, ...data, summaryRow];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        // Title style
        if (worksheet['A1'])
            worksheet['A1'].s = titleStyle;
        // Merges
        worksheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }, // Title
            { s: { r: 2, c: 2 }, e: { r: 2, c: 5 } }, // Platform group
            { s: { r: 2, c: 6 }, e: { r: 2, c: 9 } }, // Affiliate group
        ];
        // Group + header styles
        for (let c = 0; c < numCols; c++) {
            const groupCell = XLSX.utils.encode_cell({ r: 2, c });
            const headerCell = XLSX.utils.encode_cell({ r: 3, c });
            if (worksheet[groupCell])
                worksheet[groupCell].s = groupHeaderStyle;
            if (worksheet[headerCell])
                worksheet[headerCell].s = columnHeaderStyle;
        }
        // Variance color — data rows
        const dataStartRow = 4;
        rows.forEach((row, index) => {
            const rowIndex = dataStartRow + index;
            // Platform variance (col 5)
            const platVarCell = XLSX.utils.encode_cell({ r: rowIndex, c: 5 });
            if (worksheet[platVarCell]) {
                const val = Number(row.platform?.variance_percent) || 0;
                worksheet[platVarCell].s = val >= 0 ? positiveVarianceStyle : negativeVarianceStyle;
            }
            // Affiliate variance (col 9)
            const affVarCell = XLSX.utils.encode_cell({ r: rowIndex, c: 9 });
            if (worksheet[affVarCell]) {
                const val = Number(row.affiliate?.variance_percent) || 0;
                worksheet[affVarCell].s = val >= 0 ? positiveVarianceStyle : negativeVarianceStyle;
            }
        });
        // Summary row styling
        const summaryRowIndex = dataStartRow + rows.length;
        for (let c = 0; c < numCols; c++) {
            const cellRef = XLSX.utils.encode_cell({ r: summaryRowIndex, c });
            if (worksheet[cellRef])
                worksheet[cellRef].s = summaryRowStyle;
        }
        // Column widths
        worksheet['!cols'] = [
            { wch: 25 }, { wch: 18 },
            { wch: 16 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
            { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
            { wch: 15 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Affiliate Performance');
        const safeMonth = month?.replace('-', '_') || 'all';
        XLSX.writeFile(workbook, `Affiliate_Account_Performance_${safeMonth}.xlsx`);
    }
    catch (error) {
        console.error('Export failed:', error);
        toast.info('Failed to export. Please try again.');
    }
}
export default exportAffiliateProjection;
